/**
 * ResortDetector - Matches GPS coordinates to known ski resorts.
 *
 * @typedef {import('../types/index.js').Resort} Resort
 */

const EARTH_RADIUS_M = 6_371_000;

/**
 * Convert degrees to radians.
 * @param {number} deg
 * @returns {number}
 */
function toRad(deg) {
  return (deg * Math.PI) / 180;
}

/**
 * Haversine distance between two lat/lng pairs.
 * @param {number} lat1
 * @param {number} lng1
 * @param {number} lat2
 * @param {number} lng2
 * @returns {number} Distance in meters
 */
export function haversineDistance(lat1, lng1, lat2, lng2) {
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return EARTH_RADIUS_M * c;
}

/**
 * Default resort database with 15 major ski resorts worldwide.
 * Coordinates point to the approximate village/base area center.
 * @type {Resort[]}
 */
export const DEFAULT_RESORTS = [
  {
    id: 'whistler',
    name: 'Whistler Blackcomb',
    country: 'CA',
    region: 'British Columbia',
    latitude: 50.1163,
    longitude: -122.9574,
    baseElevation: 675,
    peakElevation: 2284,
    verticalDrop: 1609,
    totalRuns: 200,
    totalLifts: 37,
    websiteUrl: 'https://www.whistlerblackcomb.com',
  },
  {
    id: 'vail',
    name: 'Vail',
    country: 'US',
    region: 'Colorado',
    latitude: 39.6403,
    longitude: -106.3742,
    baseElevation: 2457,
    peakElevation: 3527,
    verticalDrop: 1070,
    totalRuns: 195,
    totalLifts: 31,
    websiteUrl: 'https://www.vail.com',
  },
  {
    id: 'park-city',
    name: 'Park City Mountain',
    country: 'US',
    region: 'Utah',
    latitude: 40.6514,
    longitude: -111.508,
    baseElevation: 2080,
    peakElevation: 3048,
    verticalDrop: 968,
    totalRuns: 330,
    totalLifts: 41,
    websiteUrl: 'https://www.parkcitymountain.com',
  },
  {
    id: 'chamonix',
    name: 'Chamonix Mont-Blanc',
    country: 'FR',
    region: 'Auvergne-Rhone-Alpes',
    latitude: 45.9237,
    longitude: 6.8694,
    baseElevation: 1035,
    peakElevation: 3842,
    verticalDrop: 2807,
    totalRuns: 119,
    totalLifts: 47,
    websiteUrl: 'https://www.chamonix.com',
  },
  {
    id: 'zermatt',
    name: 'Zermatt',
    country: 'CH',
    region: 'Valais',
    latitude: 46.0207,
    longitude: 7.7491,
    baseElevation: 1620,
    peakElevation: 3883,
    verticalDrop: 2263,
    totalRuns: 145,
    totalLifts: 52,
    websiteUrl: 'https://www.zermatt.ch',
  },
  {
    id: 'verbier',
    name: 'Verbier',
    country: 'CH',
    region: 'Valais',
    latitude: 46.0964,
    longitude: 7.2283,
    baseElevation: 1500,
    peakElevation: 3330,
    verticalDrop: 1830,
    totalRuns: 89,
    totalLifts: 34,
    websiteUrl: 'https://www.verbier.ch',
  },
  {
    id: 'niseko',
    name: 'Niseko United',
    country: 'JP',
    region: 'Hokkaido',
    latitude: 42.8625,
    longitude: 140.6987,
    baseElevation: 260,
    peakElevation: 1308,
    verticalDrop: 1048,
    totalRuns: 61,
    totalLifts: 30,
    websiteUrl: 'https://www.niseko.ne.jp',
  },
  {
    id: 'hakuba',
    name: 'Hakuba Valley',
    country: 'JP',
    region: 'Nagano',
    latitude: 36.6983,
    longitude: 137.8319,
    baseElevation: 760,
    peakElevation: 1831,
    verticalDrop: 1071,
    totalRuns: 137,
    totalLifts: 57,
    websiteUrl: 'https://www.hakubavalley.com',
  },
  {
    id: 'st-anton',
    name: 'St. Anton am Arlberg',
    country: 'AT',
    region: 'Tyrol',
    latitude: 47.1277,
    longitude: 10.2639,
    baseElevation: 1304,
    peakElevation: 2811,
    verticalDrop: 1507,
    totalRuns: 131,
    totalLifts: 34,
    websiteUrl: 'https://www.stantonamarlberg.com',
  },
  {
    id: 'kitzbuehel',
    name: 'Kitzbuhel',
    country: 'AT',
    region: 'Tyrol',
    latitude: 47.4561,
    longitude: 12.3922,
    baseElevation: 800,
    peakElevation: 2000,
    verticalDrop: 1200,
    totalRuns: 170,
    totalLifts: 57,
    websiteUrl: 'https://www.kitzbuehel.com',
  },
  {
    id: 'les-3-vallees',
    name: 'Les 3 Vallees',
    country: 'FR',
    region: 'Auvergne-Rhone-Alpes',
    latitude: 45.3335,
    longitude: 6.5657,
    baseElevation: 1300,
    peakElevation: 3230,
    verticalDrop: 1930,
    totalRuns: 328,
    totalLifts: 166,
    websiteUrl: 'https://www.les3vallees.com',
  },
  {
    id: 'val-disere',
    name: "Val d'Isere",
    country: 'FR',
    region: 'Auvergne-Rhone-Alpes',
    latitude: 45.4488,
    longitude: 6.9806,
    baseElevation: 1850,
    peakElevation: 3456,
    verticalDrop: 1606,
    totalRuns: 78,
    totalLifts: 42,
    websiteUrl: 'https://www.valdisere.com',
  },
  {
    id: 'mammoth',
    name: 'Mammoth Mountain',
    country: 'US',
    region: 'California',
    latitude: 37.6308,
    longitude: -119.0326,
    baseElevation: 2424,
    peakElevation: 3369,
    verticalDrop: 945,
    totalRuns: 150,
    totalLifts: 25,
    websiteUrl: 'https://www.mammothmountain.com',
  },
  {
    id: 'jackson-hole',
    name: 'Jackson Hole',
    country: 'US',
    region: 'Wyoming',
    latitude: 43.5877,
    longitude: -110.828,
    baseElevation: 1924,
    peakElevation: 3185,
    verticalDrop: 1261,
    totalRuns: 133,
    totalLifts: 13,
    websiteUrl: 'https://www.jacksonhole.com',
  },
  {
    id: 'aspen',
    name: 'Aspen Snowmass',
    country: 'US',
    region: 'Colorado',
    latitude: 39.1911,
    longitude: -106.8175,
    baseElevation: 2422,
    peakElevation: 3813,
    verticalDrop: 1391,
    totalRuns: 337,
    totalLifts: 43,
    websiteUrl: 'https://www.aspensnowmass.com',
  },
];

/**
 * Find the nearest resort to a given GPS coordinate.
 * Returns null if no resort is within the threshold distance.
 *
 * @param {number} lat - Latitude
 * @param {number} lng - Longitude
 * @param {Resort[]} [resortDatabase] - Resort list (defaults to DEFAULT_RESORTS)
 * @param {number} [maxDistanceM=5000] - Maximum search radius in meters
 * @returns {Resort | null}
 */
export function detectResort(lat, lng, resortDatabase = DEFAULT_RESORTS, maxDistanceM = 5000) {
  if (!resortDatabase || resortDatabase.length === 0) return null;

  let closest = null;
  let closestDist = Infinity;

  for (const resort of resortDatabase) {
    const dist = haversineDistance(lat, lng, resort.latitude, resort.longitude);
    if (dist < closestDist) {
      closestDist = dist;
      closest = resort;
    }
  }

  if (closestDist <= maxDistanceM) {
    return closest;
  }

  return null;
}

/**
 * Find all resorts within a given radius.
 *
 * @param {number} lat
 * @param {number} lng
 * @param {Resort[]} [resortDatabase]
 * @param {number} [radiusM=50000] - Search radius in meters (default 50km)
 * @returns {Array<{ resort: Resort, distance: number }>} Sorted by distance (ascending)
 */
export function findNearbyResorts(lat, lng, resortDatabase = DEFAULT_RESORTS, radiusM = 50_000) {
  if (!resortDatabase || resortDatabase.length === 0) return [];

  const results = [];

  for (const resort of resortDatabase) {
    const dist = haversineDistance(lat, lng, resort.latitude, resort.longitude);
    if (dist <= radiusM) {
      results.push({ resort, distance: dist });
    }
  }

  results.sort((a, b) => a.distance - b.distance);
  return results;
}

/**
 * Search resorts by name (case-insensitive substring match).
 *
 * @param {string} query
 * @param {Resort[]} [resortDatabase]
 * @returns {Resort[]}
 */
export function searchResorts(query, resortDatabase = DEFAULT_RESORTS) {
  if (!query || !resortDatabase) return [];

  const fold = (s) =>
    (s || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim();

  const lowerQuery = fold(query);
  if (lowerQuery.length === 0) return [];

  return resortDatabase.filter(
    (r) =>
      fold(r.name).includes(lowerQuery) ||
      fold(r.country).includes(lowerQuery) ||
      fold(r.countryName).includes(lowerQuery) ||
      fold(r.region).includes(lowerQuery)
  );
}
